// Pure helpers for the payloads the remove / unassign modals send — extracted
// so they can be unit-tested in isolation from React and Apollo.

/**
 * Collect every `mapping_id` attached to the given route's `orders` array,
 * skipping mappings without an id (shouldn't happen in practice, but defensive
 * against partially-loaded cache entries).
 *
 * Used by RouteRemoveModal to build the `mapping_ids_delete` argument for the
 * REMOVE_ROUTE mutation — every mapping on the route gets deleted because the
 * route itself is being deleted.
 *
 * @param {object} route
 * @returns {string[]} mapping_id values in order
 */
export const collectMappingIdsFromRoute = (route) => {
    return (route?.orders || []).filter((mapping) => mapping?.mapping_id).map((mapping) => mapping.mapping_id);
};

/**
 * Collect every `mapping_id` on the given route whose `order_id` is part of
 * the stop being removed. Used by StopRemoveModal to build the
 * `mapping_ids_delete` argument for the UPDATE_ROUTE_STOPS mutation — only the
 * mappings whose orders belong to the removed stop get deleted; other
 * mappings on the route stay put.
 *
 * @param {object} route - the route containing the stop
 * @param {object} stop - the stop being removed; must have `stop.orders` (array of order_ids)
 * @returns {string[]} mapping_id values in order
 */
export const collectMappingIdsForStop = (route, stop) => {
    const stopOrderIds = stop?.orders || [];
    return (route?.orders || [])
        .filter(({ order_id }) => stopOrderIds.includes(order_id))
        .filter((mapping) => mapping?.mapping_id)
        .map((mapping) => mapping.mapping_id);
};

// Returns a delivery-leg order to its pre-assignment state when its route is emptied or deleted.
//
// `resource_id` is nulled for the same reason useAssignToRoute's unassign path nulls it: an order
// left pointing at a resource keeps consuming that resource's day while sitting on no route.
// `delivery_date` is left alone so the order returns to the day's unassigned bucket rather than the
// unscheduled pool. Callers scope this to non-PICKUP mappings — the leg that claimed the resource is
// the leg that releases it.
export const buildDeliveryUnassignSet = (order) => ({
    del_window_start: null,
    del_window_end: null,
    original_del_window_start: null,
    original_del_window_end: null,
    delivery_time_confirmed: null,
    confirmation_source: null,
    resource_id: null,
    order_status: order?.carrier_id ? 'claimed' : 'pending',
    // Mirrors partnerAssignmentUpdates' unassign branch: releasing the partner just clears
    // contractor_id, carrier_id is never touched.
    ...(order?.contractor_id ? { contractor_id: null } : {}),
});

// OrdersToUnassigned's write: the row loses its date entirely. Dropping the delivery date releases
// the resource too, as it does in OrdersReschedule — an order with no delivery day would otherwise
// keep counting against the resource-day it left.
export const buildSendToUnassignedSet = (order) => ({
    ...(order?.crossdock_leg === 'pickup'
        ? {
              pickup_date: null,
              pickup_planning: false,
          }
        : {
              del_window_end: null,
              del_window_start: null,
              delivery_date: null,
              delivery_time_confirmed: null,
              confirmation_source: null,
              original_del_window_end: null,
              original_del_window_start: null,
              planning: false,
              resource_id: null,
          }),
    order_status: order?.oms ? 'pending' : 'claimed',
});
